/**
 * The row under the deck frame, and the card on the last slide.
 *
 * Prototype: markup 278 (`.deckrow`) and 279 (`.watchcard`), CSS 43–48. It sits
 * **below** the frame and never overlays it — a deck is read, and an overlay on
 * the thing being read is a tax on every slide to serve one click.
 *
 * The slide number is captured at click time and travels with the turn, so
 * paging on while the answer streams cannot rewrite which slide was asked
 * about.
 */

import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { MessageSquare, PlayCircle } from 'lucide-react'
import { useState } from 'react'
import { trackEvent as trackGaEvent } from '../../../lib/gtag'
import { buildSlideQuestion } from '../config/preset-questions'
import { useAskProfile } from '../context/ask-profile-context'

export interface DeckSlideRowProps {
  /** 1-based, as the viewer reports it. */
  page: number
  totalPages: number
  /** The deck's document id — the `resourceId` of the slide-ask event. */
  documentId: string
  /** Omitted when this profile has no pitch recording to link to. */
  onWatchPitch?: () => void
}

export function DeckSlideRow({ page, totalPages, documentId, onWatchPitch }: DeckSlideRowProps) {
  const ask = useAskProfile()
  const [deckEndDismissed, setDeckEndDismissed] = useState(false)

  const isLastSlide = totalPages > 0 && page === totalPages

  return (
    <div className="pt-2">
      <div className="flex items-center justify-between gap-2.5 px-0.5 text-xs text-muted-foreground">
        <span>
          Slide {page} of {totalPages}
        </span>
        <div className="flex items-center gap-3.5">
          {onWatchPitch && (
            <button
              type="button"
              onClick={onWatchPitch}
              className="inline-flex items-center gap-1.5 py-1 text-xs font-medium text-foreground transition-colors hover:underline"
            >
              <PlayCircle className="size-3.5" aria-hidden />
              Watch the full pitch recording
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              ask.trackEvent('ask_profile_slide_ask', documentId, { page, totalPages })
              // No slide number to Google: with a known deck it identifies the
              // company as surely as its name would.
              trackGaEvent('ask_profile_slide_asked')
              ask.ask({ entry: 'slide', slideNumber: page, text: buildSlideQuestion(page) })
            }}
            className="inline-flex items-center gap-1.5 py-1 text-xs font-medium text-foreground transition-colors hover:underline"
          >
            <MessageSquare className="size-3.5" aria-hidden />
            Ask about slide {page}
          </button>
        </div>
      </div>

      {isLastSlide && !deckEndDismissed && (
        <div className="mt-2.5 flex flex-wrap items-center gap-3 rounded-xl border border-primary/35 bg-primary/[0.06] px-3.5 py-3 text-[13px]">
          <div className="min-w-[200px] flex-1">
            <b className="block text-[13.5px] font-semibold text-foreground">
              That was the last slide.
            </b>
            <span className="text-muted-foreground">
              Have an agent follow {ask.startupName}: it reads every update and tells you what
              changed and why it matters to you.
            </span>
          </div>
          <CtaButton size="sm" onClick={() => ask.openDoor('agent_follow', 'deck_end')}>
            Follow this company
          </CtaButton>
          <SecondaryButton size="sm" onClick={() => setDeckEndDismissed(true)}>
            Not now
          </SecondaryButton>
        </div>
      )}
    </div>
  )
}
